import { useEffect, useState } from "react";
import { Plus, Pencil, Printer, Trash2, RotateCcw, Undo2, FileText, Download } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { PageHeader } from "../components/ui/PageHeader";
import { Select } from "../components/ui/Select";
import { CurrencySelect } from "../components/ui/CurrencySelect";
import { DataTable, type Column } from "../components/ui/DataTable";
import { TableExportToolbar } from "../components/TableExportToolbar";
import { PrintTable } from "../components/PrintTable";
import { PreviewModal } from "../components/PreviewModal";
import { RowActions } from "../components/ui/RowActions";
import { Lightbox } from "../components/ui/Lightbox";
import { ScanImportButton } from "../components/scan/ScanImportButton";
import { ScanImportModal } from "../components/scan/ScanImportModal";
import { eur, fmtCurrency, fmtDate, fmtTime, nlStatus } from "../lib/format";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const methodLabels: Record<string, string> = {
  BANK_TRANSFER: "Bankoverschrijving",
  CASH: "Contant",
  CREDIT_CARD: "Creditcard",
  IDEAL: "iDEAL",
  PAYPAL: "PayPal",
  OTHER: "Overige",
};

export default function Payments() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "SUPERADMIN";
  const [payments, setPayments] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [visibleRows, setVisibleRows] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [lightbox, setLightbox] = useState<{ src: string; fileName: string } | null>(null);

  const [invoiceId, setInvoiceId] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [method, setMethod] = useState("BANK_TRANSFER");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  // Multi-valuta: valuta + wisselkoers (EUR per 1 eenheid currency).
  const [currency, setCurrency] = useState("EUR");
  const [exchangeRate, setExchangeRate] = useState<string>("1");
  const [rateLoading, setRateLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  /** Haal de EUR-koers op voor een valuta op een datum (frankfurter). */
  const fetchRate = async (cur: string, rateDate: string) => {
    if (!cur || cur.toUpperCase() === "EUR") { setExchangeRate("1"); return; }
    setRateLoading(true);
    try {
      const { data } = await api.get("/api/exchange-rate", { params: { from: cur, date: rateDate } });
      if (typeof data?.rate === "number" && data.rate > 0) setExchangeRate(String(data.rate));
    } catch { /* laat handmatige waarde staan */ }
    finally { setRateLoading(false); }
  };
  const onCurrencyChange = (cur: string) => { setCurrency(cur); fetchRate(cur, date); };

  useEffect(() => {
    loadPayments();
    loadAllInvoices();
  }, []);

  const loadPayments = () => api.get("/api/payments").then((res) => setPayments(res.data));
  const loadAllInvoices = () => api.get("/api/invoices").then((res) => setInvoices(res.data));
  const loadOpenInvoices = () =>
    api.get("/api/invoices").then((res) => setInvoices(res.data.filter((i: any) => i.status !== "PAID")));

  const handleSave = async () => {
    setSaveError("");
    if (!invoiceId) { setSaveError("Kies een factuur."); return; }
    if (!(Number(amount) > 0)) { setSaveError("Vul een bedrag groter dan 0 in."); return; }
    setSaving(true);
    try {
      const payload = {
        invoiceId,
        amount: Number(amount),
        date,
        method,
        reference,
        notes,
        currency: currency || "EUR",
        ...(Number(exchangeRate) > 0 ? { exchangeRate: Number(exchangeRate) } : {}),
      };
      if (editing) {
        await api.put(`/api/payments/${editing.id}`, payload);
      } else {
        await api.post("/api/payments", payload);
      }
      setIsOpen(false);
      resetForm();
      loadPayments();
      if (editing) loadAllInvoices(); else loadOpenInvoices();
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || "Opslaan mislukt. Controleer de velden en probeer opnieuw.");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setInvoiceId("");
    setAmount("");
    setReference("");
    setNotes("");
    setDate(new Date().toISOString().split("T")[0]);
    setMethod("BANK_TRANSFER");
    setCurrency("EUR");
    setExchangeRate("1");
    setRateLoading(false);
    setEditing(null);
    setSaveError("");
  };

  const openNew = () => {
    resetForm();
    loadOpenInvoices();
    setIsOpen(true);
  };

  const openEdit = (p: any) => {
    setEditing(p);
    loadAllInvoices();
    setInvoiceId(p.invoiceId || "");
    setAmount(String(p.amount ?? ""));
    setDate(p.date ? new Date(p.date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0]);
    setMethod(p.method || "BANK_TRANSFER");
    setReference(p.reference || "");
    setNotes(p.notes || "");
    setCurrency(p.currency || "EUR");
    setExchangeRate(p.exchangeRate != null ? String(p.exchangeRate) : "1");
    setSaveError("");
    setIsOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Weet je zeker dat je deze betaling wilt verwijderen? Het betaalde bedrag van de factuur wordt opnieuw berekend.")) return;
    await api.delete(`/api/payments/${id}`);
    loadPayments();
    loadAllInvoices();
  };

  const handleViewPdf = async (p: any) => {
    try {
      const res = await api.get(`/api/payments/${p.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert("PDF laden mislukt.");
    }
  };

  const handleDownloadPdf = async (p: any) => {
    try {
      const res = await api.get(`/api/payments/${p.id}/pdf`, { responseType: "blob", params: { download: "1" } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `betaling.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert("PDF downloaden mislukt.");
    }
  };

  const handleReverse = async (p: any) => {
    const reason = window.prompt(
      `Betaling van € ${Number(p.amount).toFixed(2)} terugboeken?\n\nDit maakt een storno (TERUGBETALING) aan die de oorspronkelijke betaling tegengaat. Het betaalde bedrag van de gekoppelde factuur wordt opnieuw berekend. Geef optioneel een reden:`,
      ""
    );
    if (reason === null) return;
    try {
      await api.post(`/api/payments/${p.id}/reverse`, { reason: reason.trim() || undefined });
      loadPayments();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Terugboeken mislukt.");
    }
  };

  const handleUndoReverse = async (p: any) => {
    if (
      !confirm(
        "Weet je zeker dat je deze terugboeking (storno) ongedaan wilt maken?\n\nDe storno wordt verwijderd, zodat de oorspronkelijke betaling weer volledig meetelt. Het betaalde bedrag van de gekoppelde factuur wordt opnieuw berekend."
      )
    )
      return;
    try {
      await api.post(`/api/payments/${p.id}/undo-reverse`);
      loadPayments();
      loadAllInvoices();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Ongedaan maken mislukt.");
    }
  };

  /** BTW-soort uit de factuurregels: unieke tariefpercentages, bijv. "21%, 9%". */
  const vatTypesOf = (p: any): string => {
    const lines = p.invoice?.lines as any[] | undefined;
    if (!lines || !lines.length) return "—";
    const pcts = [...new Set(lines.map((l) => Number(l.vatRate?.percentage)).filter((n) => !isNaN(n)))];
    pcts.sort((a, b) => b - a);
    return pcts.length ? pcts.map((n) => `${n}%`).join(", ") : "—";
  };

  const exportRows = selected.length ? selected : visibleRows;

  const columns: Column<any>[] = [
    { key: "type", header: "Type", accessor: (p) => (p.type === "REFUND" ? "Terugbetaling" : "Betaling"), sortable: false,
      render: (p) => p.type === "REFUND" ? (
        <Badge className="bg-rose-50/80 text-rose-700 border-rose-200">Terugbetaling</Badge>
      ) : (
        <Badge className="bg-emerald-50/80 text-emerald-700 border-emerald-200">Betaling</Badge>
      ) },
    { key: "number", header: "Referentie", accessor: (p) => p.reference || p.invoice?.number || "—" },
    { key: "date", header: "Datum", accessor: (p) => p.date ? fmtDate(p.date) : "—",
      sortValue: (p) => new Date(p.date).getTime() },
    { key: "time", header: "Tijd", accessor: (p) => p.date ? fmtTime(p.date) : "—",
      sortValue: (p) => new Date(p.date).getTime(), sortable: false },
    { key: "party", header: "Klant / leverancier",
      accessor: (p) => p.contact?.name || p.invoice?.contact?.companyName || "—" },
    { key: "amount", header: "Bedrag",
      accessor: (p) => eur(p.type === "REFUND" ? -(Number(p.amountEur) || Number(p.amount)) : (Number(p.amountEur) || Number(p.amount))),
      className: "text-right",
      render: (p) => {
        const signed = p.type === "REFUND" ? -Number(p.amount) : Number(p.amount);
        const signedEur = p.type === "REFUND" ? -(Number(p.amountEur) || 0) : (Number(p.amountEur) || 0);
        return (
          <div className={`text-right leading-tight ${p.type === "REFUND" ? "text-rose-600 font-semibold" : ""}`}>
            <div>{fmtCurrency(signed, p.currency)}</div>
            {p.currency && p.currency !== "EUR" && p.amountEur != null && (
              <div className="text-xs text-slate-400">≈ {eur(signedEur)}</div>
            )}
          </div>
        );
      },
      sortValue: (p) => (p.type === "REFUND" ? -1 : 1) * (Number(p.amountEur) || Number(p.amount)) },
    { key: "net", header: "Netto", accessor: (p) => p.invoice?.subtotal != null ? eur(p.invoice.subtotal) : "—",
      className: "text-right", sortValue: (p) => Number(p.invoice?.subtotal ?? 0) },
    { key: "vat", header: "BTW", accessor: (p) => p.invoice?.vatTotal != null ? eur(p.invoice.vatTotal) : "—",
      className: "text-right", sortValue: (p) => Number(p.invoice?.vatTotal ?? 0) },
    { key: "gross", header: "Bruto", accessor: (p) => p.invoice?.total != null ? eur(p.invoice.total) : "—",
      className: "text-right", sortValue: (p) => Number(p.invoice?.total ?? 0) },
    { key: "vatType", header: "BTW-soort", accessor: (p) => vatTypesOf(p), sortable: false },
    { key: "method", header: "Methode", accessor: (p) => methodLabels[p.method] || p.method || "—" },
    { key: "invoiceDue", header: "Factuur vervaldatum",
      accessor: (p) => p.invoice?.dueDate ? fmtDate(p.invoice.dueDate) : "—",
      sortValue: (p) => p.invoice?.dueDate ? new Date(p.invoice.dueDate).getTime() : 0 },
    { key: "status", header: "Status", accessor: (p) => nlStatus(p.invoice?.status),
      render: (p) => p.invoice?.status ? (
        <Badge status={p.invoice.status === "PAID" ? "PAID" : "SENT"}>{nlStatus(p.invoice.status)}</Badge>
      ) : <span className="text-slate-400">—</span> },
    { key: "doc", header: "Document", accessor: () => "", sortable: false, width: "1%",
      render: (p) => p.receiptUrl ? (
        <button
          onClick={() => {
            const url = `${import.meta.env.VITE_API_URL}${p.receiptUrl}`;
            if (url.toLowerCase().endsWith(".pdf")) window.open(url, "_blank");
            else setLightbox({ src: url, fileName: p.receiptUrl?.split("/").pop() || "document" });
          }}
          className="p-1 rounded-lg hover:bg-slate-100 transition-colors"
          title="Document bekijken"
        >
          {p.receiptUrl.toLowerCase().endsWith(".pdf") ? (
            <FileText size={22} className="text-slate-500" />
          ) : (
            <img src={`${import.meta.env.VITE_API_URL}${p.receiptUrl}`} alt="Betalingsbewijs" className="h-10 w-10 object-cover rounded-md border border-slate-200" />
          )}
        </button>
      ) : <span className="text-slate-300">—</span> },
    { key: "actions", header: "Acties", accessor: () => "", sortable: false, width: "1%",
      render: (p) => (
        <div className="flex items-center gap-1">
          <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-600" title="Bewerken">
            <Pencil size={20} />
          </button>
          <RowActions
            items={[
              { label: "PDF openen", icon: FileText, onClick: () => handleViewPdf(p) },
              { label: "Download PDF", icon: Download, onClick: () => handleDownloadPdf(p) },
              { label: "Afdrukken / PDF", icon: Printer, onClick: () => setPreviewId(p.id) },
              { label: "Terugboeken", icon: RotateCcw, onClick: () => handleReverse(p), hidden: p.type === "REFUND" },
              { label: "Terugboeking ongedaan maken", icon: Undo2, onClick: () => handleUndoReverse(p), hidden: p.type !== "REFUND" },
              { label: "Verwijderen", icon: Trash2, onClick: () => handleDelete(p.id), hidden: !isSuperadmin },
            ]}
          />
        </div>
      ) },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Betalingen"
        subtitle="Registreer inkomende betalingen"
        action={
          <Button onClick={openNew}>
            <Plus size={27} /> Nieuwe betaling
          </Button>
        }
      />

      <DataTable
        rows={payments}
        columns={columns}
        rowKey={(p) => p.id}
        searchPlaceholder="Zoek betalingen..."
        storageKey="payments"
        initialSort={{ key: "date", dir: "desc" }}
        groupBy={{ getDate: (p) => p.date, getAmount: (p) => Number(p.amountEur ?? p.amount) }}
        emptyMessage="Nog geen betalingen. Registreer een betaling om openstaande facturen op nul te zetten."
        onVisibleRowsChange={setVisibleRows}
        selectable
        onSelectionChange={setSelected}
        toolbar={
          <>
            <ScanImportButton onClick={() => setScanOpen(true)} />
            <TableExportToolbar
              rows={exportRows}
              columns={columns}
              filename={selected.length ? `betalingen-selectie` : "betalingen"}
              source="betalingen"
              previewKind="payments"
            />
          </>
        }
      />

      <PrintTable rows={exportRows} columns={columns} title="Betalingen" />

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editing ? "Betaling bewerken" : "Nieuwe betaling"}
        subtitle={editing ? "Pas de betaling aan — factuur wordt herberekend" : "Koppel een betaling aan een openstaande factuur"}
        size="md"
      >
        <div className="space-y-4">
          <Select
            label="Factuur"
            options={[
              { value: "", label: editing ? "Geen (losse betaling)" : "Kies openstaande factuur" },
              ...invoices.map((i) => ({
                value: i.id,
                label: `${i.number} — € ${Math.max(0, Number(i.total || 0) - Number(i.paidAmount || 0)).toFixed(2)} open`,
              })),
            ]}
            value={invoiceId}
            onChange={(e) => {
              setInvoiceId(e.target.value);
              if (!editing) {
                const inv = invoices.find((i) => i.id === e.target.value);
                if (inv) {
                  setAmount(String(Math.max(0, Number(inv.total || 0) - Number(inv.paidAmount || 0))));
                  // Neem de valuta van de factuur over + koers ophalen.
                  const invCur = inv.currency || "EUR";
                  setCurrency(invCur);
                  fetchRate(invCur, date);
                }
              }
            }}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input label="Bedrag" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <Input label="Datum" type="date" value={date} onChange={(e) => { setDate(e.target.value); if (currency && currency !== "EUR") fetchRate(currency, e.target.value); }} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <CurrencySelect label="Valuta" value={currency} onChange={(e) => onCurrencyChange(e.target.value)} />
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Wisselkoers (EUR per 1 {currency})
              </label>
              <div className="flex items-center gap-2">
                <Input
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  disabled={currency === "EUR"}
                />
                {rateLoading && <span className="text-xs text-slate-400">…</span>}
              </div>
            </div>
          </div>
          {currency !== "EUR" && Number(amount) > 0 && Number(exchangeRate) > 0 && (
            <div className="text-sm text-slate-500 -mt-2">≈ {eur(Number(amount) * Number(exchangeRate))} in EUR</div>
          )}

          <Select
            label="Betaalmethode"
            options={Object.entries(methodLabels).map(([value, label]) => ({ value, label }))}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          />

          <Input label="Referentie" value={reference} onChange={(e) => setReference(e.target.value)} />

          <Input label="Notities" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optioneel" />

          {saveError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{saveError}</p>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={saving}>Annuleren</Button>
            <Button onClick={handleSave} loading={saving}>{editing ? "Wijzigingen opslaan" : "Betaling registreren"}</Button>
          </div>
        </div>
      </Modal>

      <PreviewModal
        isOpen={!!previewId}
        onClose={() => setPreviewId(null)}
        kind="payments"
        id={previewId}
        title="Betalingsbewijs"
      />

      <ScanImportModal
        isOpen={scanOpen}
        onClose={() => setScanOpen(false)}
        mode="payment"
        onSaved={() => { loadPayments(); loadAllInvoices(); }}
        onEditCreated={openEdit}
      />

      <Lightbox
        isOpen={!!lightbox}
        onClose={() => setLightbox(null)}
        src={lightbox?.src || ""}
        fileName={lightbox?.fileName}
      />
    </div>
  );
}
